import { useMemo, useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, FormControl,
  FormControlLabel, IconButton, InputLabel, MenuItem, Radio, RadioGroup,
  Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import type {
  SuperAdminOrg, SuperAdminOrgDetail, SuperAdminUser, SuperAdminRole,
} from '@cmms/api-client'

import { api } from '../../lib/api'
import { APP_NAME } from '../../lib/branding'
import { toast } from '../../store/snackbarStore'
import { SearchableSelect } from '../../components/SearchableSelect'

export function OrganizationsTab() {
  const [activeSlug, setActiveSlug] = useState<string | null>(null)

  if (activeSlug) {
    return <OrgDetail slug={activeSlug} onBack={() => setActiveSlug(null)} />
  }
  return <OrgList onSelect={setActiveSlug} />
}

// ─── Master list ────────────────────────────────────────────────────────────

function OrgList({ onSelect }: { onSelect: (slug: string) => void }) {
  const orgs = useQuery({
    queryKey: ['super-admin', 'orgs'],
    queryFn: () => api.superAdmin.listAllOrgs(),
  })

  const items = orgs.data ?? []

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
            All organizations
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Manage every {APP_NAME} workspace from one place — rename, message, delete, or jump into the user roster.
          </Typography>
        </Box>
        <Button
          onClick={() => orgs.refetch()}
          startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
          variant="text"
          sx={{ color: 'text.secondary', textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Stack>

      {orgs.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <EmptyOrgs />
      ) : (
        <Stack spacing={1}>
          {items.map((org) => (
            <OrgRow key={org.id} org={org} onOpen={() => onSelect(org.slug)} />
          ))}
        </Stack>
      )}
    </Box>
  )
}

function EmptyOrgs() {
  return (
    <Box sx={{ py: 8, textAlign: 'center' }}>
      <Box sx={{
        display: 'inline-flex', width: 56, height: 56,
        borderRadius: 99, bgcolor: 'action.hover',
        alignItems: 'center', justifyContent: 'center', mb: 2,
      }}>
        <BusinessOutlinedIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        No organizations yet
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
        Approve a signup request to create the first one.
      </Typography>
    </Box>
  )
}

function OrgRow({ org, onOpen }: { org: SuperAdminOrg; onOpen: () => void }) {
  const initials = (org.name ?? org.slug).slice(0, 2).toUpperCase()
  return (
    <Box
      onClick={onOpen}
      sx={{
        p: 2, borderRadius: 2, border: '1px solid',
        borderColor: 'divider', bgcolor: 'background.paper',
        display: 'flex', alignItems: 'center', gap: 2,
        cursor: 'pointer',
        transition: 'border-color 120ms ease, transform 120ms ease',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Avatar sx={{
        bgcolor: 'primary.light', color: 'primary.main',
        fontSize: 13, fontWeight: 700, width: 38, height: 38,
      }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.005em',
        }} noWrap>
          {org.name}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
          <Typography sx={{
            fontSize: 12, color: 'text.secondary',
            fontFamily: 'ui-monospace, monospace',
          }}>
            {org.slug}
          </Typography>
          <Chip size="small" label={`${org.user_count ?? 0} users`}
            sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />
          {org.admin_email && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
              · admin: {org.admin_email}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  )
}

// ─── Detail panel ───────────────────────────────────────────────────────────

function OrgDetail({ slug, onBack }: { slug: string; onBack: () => void }) {
  const qc = useQueryClient()
  const detail = useQuery({
    queryKey: ['super-admin', 'org', slug],
    queryFn: () => api.superAdmin.getOrgDetail(slug),
  })

  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [notifyOpen, setNotifyOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const rename = useMutation({
    mutationFn: (name: string) => api.superAdmin.renameOrg(slug, name),
    onSuccess: () => {
      toast.success('Workspace renamed')
      setEditingName(false)
      qc.invalidateQueries({ queryKey: ['super-admin', 'org', slug] })
      qc.invalidateQueries({ queryKey: ['super-admin', 'orgs'] })
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  const downloadReport = useMutation({
    mutationFn: () => api.superAdmin.downloadOrgReportPdf(slug),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${slug}-report.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    onError: (e: unknown) => toast.error((e as Error).message ?? 'Failed to generate report'),
  })

  if (detail.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }
  if (detail.isError || !detail.data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>
          Couldn't load this workspace.
        </Typography>
        <Button onClick={onBack} startIcon={<ArrowBackIcon />}>Back</Button>
      </Box>
    )
  }

  const org = detail.data

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          All organizations
        </Typography>
      </Stack>

      <Box sx={{ mb: 3 }}>
        {editingName ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              size="small" autoFocus
              sx={{ '& .MuiInputBase-input': { fontSize: 22, fontWeight: 700 } }}
            />
            <Button
              variant="contained"
              onClick={() => rename.mutate(nameDraft.trim())}
              disabled={!nameDraft.trim() || rename.isPending}
            >
              {rename.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={() => setEditingName(false)}>Cancel</Button>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{
              fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em',
            }}>
              {org.name}
            </Typography>
            <Tooltip title="Rename workspace">
              <IconButton size="small" onClick={() => {
                setNameDraft(org.name)
                setEditingName(true)
              }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <Typography sx={{
          fontSize: 13, color: 'text.secondary',
          fontFamily: 'ui-monospace, monospace', mt: 0.5,
        }}>
          {org.slug}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
        Overview
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <StatTile label="Active users"        value={`${org.user_count ?? 0} / ${org.total_user_count ?? 0}`} />
        <StatTile label="Sites"               value={org.site_count ?? 0} />
        <StatTile label="Customers"           value={org.customer_count ?? 0} />
        <StatTile label="Customer groups"     value={org.customer_group_count ?? 0} />
        <StatTile label="Customer subgroups"  value={org.customer_subgroup_count ?? 0} />
        <StatTile label="Locations"           value={org.location_count ?? 0} />
        <StatTile label="Assets"              value={org.asset_count ?? 0} />
        <StatTile label="Vendors"             value={org.vendor_count ?? 0} />
        <StatTile label="Inventory parts"     value={org.part_count ?? 0} />
        <StatTile label="Teams"               value={org.team_count ?? 0} />
        <StatTile label="Checklist templates" value={org.checklist_template_count ?? 0} />
        <StatTile label="Workflows"           value={org.workflow_count ?? 0} />
      </Stack>

      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
        Work orders ({org.work_order_count ?? 0} total)
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <StatTile label="Open"        value={org.work_order_status_counts?.open ?? 0} />
        <StatTile label="In progress" value={org.work_order_status_counts?.in_progress ?? 0} />
        <StatTile label="Closed"      value={org.work_order_status_counts?.closed ?? 0} />
        <StatTile label="Resolved"    value={org.work_order_status_counts?.resolved ?? 0} />
      </Stack>

      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
        Tasks ({org.task_count ?? 0} total)
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <StatTile label="Open"        value={org.task_status_counts?.open ?? 0} />
        <StatTile label="In progress" value={org.task_status_counts?.in_progress ?? 0} />
        <StatTile label="Closed"      value={org.task_status_counts?.closed ?? 0} />
        <StatTile label="Resolved"    value={org.task_status_counts?.resolved ?? 0} />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <Button
          onClick={() => downloadReport.mutate()}
          startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
          variant="contained"
          disabled={downloadReport.isPending}
          sx={{ textTransform: 'none', borderRadius: 999 }}
        >
          {downloadReport.isPending ? 'Generating…' : 'Download report'}
        </Button>
        <Button
          onClick={() => setNotifyOpen(true)}
          startIcon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />}
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 999 }}
        >
          Send notification
        </Button>
        <Button
          onClick={() => setDeleteOpen(true)}
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          color="error" variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 999 }}
        >
          Delete workspace
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <UsersSection slug={slug} />

      <NotifyDialog
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        slug={slug}
        orgName={org.name}
      />
      <DeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        slug={slug}
        orgName={org.name}
        onDeleted={onBack}
      />
    </Box>
  )
}

function StatTile({ label, value }: { label: string; value: number | string }) {
  return (
    <Box sx={{
      flex: '1 1 150px', minWidth: 0,
      p: 2, borderRadius: 2,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
    }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 22, fontWeight: 700, lineHeight: 1.2,
        letterSpacing: '-0.02em', mt: 0.5,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </Typography>
    </Box>
  )
}

// ─── Users section ──────────────────────────────────────────────────────────

function UsersSection({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const users = useQuery({
    queryKey: ['super-admin', 'org-users', slug],
    queryFn: () => api.superAdmin.listOrgUsers(slug),
  })
  const roles = useQuery({
    queryKey: ['super-admin', 'org-roles', slug],
    queryFn: () => api.superAdmin.listOrgRoles(slug),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['super-admin', 'org-users', slug] })
    qc.invalidateQueries({ queryKey: ['super-admin', 'org', slug] })
  }

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.superAdmin.updateOrgUserRole(slug, userId, role),
    onSuccess: () => { toast.success('Role updated'); invalidate() },
    onError: (e: unknown) => toast.error((e as Error).message),
  })
  const deactivate = useMutation({
    mutationFn: (userId: string) => api.superAdmin.deactivateOrgUser(slug, userId),
    onSuccess: () => { toast.success('User deactivated'); invalidate() },
    onError: (e: unknown) => toast.error((e as Error).message),
  })
  const reactivate = useMutation({
    mutationFn: (userId: string) => api.superAdmin.reactivateOrgUser(slug, userId),
    onSuccess: () => { toast.success('User reactivated'); invalidate() },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  const [removing, setRemoving] = useState<SuperAdminUser | null>(null)
  const removeUser = useMutation({
    mutationFn: (userId: string) => api.superAdmin.removeOrgUser(slug, userId),
    onSuccess: () => {
      toast.success('User removed')
      setRemoving(null)
      invalidate()
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  const items = users.data ?? []
  const availableRoles = useMemo(
    () => (roles.data ?? []).map((r) => r.name),
    [roles.data],
  )

  return (
    <Box>
      <Typography sx={{
        fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em', mb: 1.5,
      }}>
        Members ({items.length})
      </Typography>
      {users.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} />
        </Box>
      ) : items.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2 }}>
          No users in this workspace yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              roles={availableRoles}
              onChangeRole={(role) => updateRole.mutate({ userId: u.id, role })}
              onDeactivate={() => deactivate.mutate(u.id)}
              onReactivate={() => reactivate.mutate(u.id)}
              onRemove={() => setRemoving(u)}
              busy={
                (updateRole.isPending && updateRole.variables?.userId === u.id) ||
                (deactivate.isPending && deactivate.variables === u.id) ||
                (reactivate.isPending && reactivate.variables === u.id)
              }
            />
          ))}
        </Stack>
      )}

      <Dialog open={!!removing} onClose={() => setRemoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove this user?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            <strong>{removing?.name}</strong> ({removing?.email}) will be permanently
            removed from this workspace. Their work order assignments and audit
            history will be retained but unlinked from a user record.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            onClick={() => removing && removeUser.mutate(removing.id)}
            disabled={removeUser.isPending}
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            {removeUser.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function UserRow({
  user, roles, onChangeRole, onDeactivate, onReactivate, onRemove, busy,
}: {
  user: SuperAdminUser
  roles: string[]
  onChangeRole: (role: string) => void
  onDeactivate: () => void
  onReactivate: () => void
  onRemove: () => void
  busy: boolean
}) {
  const initials = (user.name || user.email).slice(0, 2).toUpperCase()
  return (
    <Box sx={{
      p: 1.5, borderRadius: 2, border: '1px solid',
      borderColor: 'divider', bgcolor: 'background.paper',
      display: 'flex', alignItems: 'center', gap: 2,
    }}>
      <Avatar sx={{
        bgcolor: user.is_active ? 'primary.light' : 'action.hover',
        color:   user.is_active ? 'primary.main'  : 'text.secondary',
        width: 34, height: 34, fontSize: 12, fontWeight: 700,
      }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{
            fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.005em',
          }} noWrap>
            {user.name}
          </Typography>
          {!user.is_active && (
            <Chip size="small" label="Deactivated" color="warning" variant="outlined"
              sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
          )}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>
          {user.email}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 130 }}>
        <SearchableSelect
          value={user.role ?? ''}
          onChange={(next) => {
            if (next && next !== user.role) onChangeRole(next)
          }}
          options={roles}
          getValue={(r) => r}
          getLabel={(r) => r}
          placeholder="Role"
          disabled={busy || roles.length === 0}
        />
      </Box>

      {user.is_active ? (
        <Tooltip title="Deactivate (revokes their sessions)">
          <span>
            <IconButton size="small" onClick={onDeactivate} disabled={busy}>
              <PersonOffOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title="Reactivate">
          <span>
            <IconButton size="small" onClick={onReactivate} disabled={busy}>
              <RestartAltOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Remove user">
        <span>
          <IconButton size="small" onClick={onRemove} disabled={busy}>
            <PersonRemoveOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

// ─── Notify dialog ──────────────────────────────────────────────────────────

function NotifyDialog({
  open, onClose, slug, orgName,
}: {
  open: boolean
  onClose: () => void
  slug: string
  orgName: string
}) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<'admins' | 'all'>('admins')

  const send = useMutation({
    mutationFn: () => api.superAdmin.notifyOrg(slug, { subject, body, audience }),
    onSuccess: (res) => {
      toast.success(`Sent to ${res.sent} of ${res.total} ${
        res.audience === 'admins' ? 'admin(s)' : 'member(s)'
      }`)
      setSubject(''); setBody('')
      onClose()
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Send a message to {orgName}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 13, mb: 2 }}>
          Sent from <em>{APP_NAME} administration</em>. Use the audience picker to choose
          between just the org admins or every active member.
        </DialogContentText>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="audience-label">Audience</InputLabel>
          <Select
            labelId="audience-label" label="Audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value as 'admins' | 'all')}
          >
            <MenuItem value="admins">Org admins only</MenuItem>
            <MenuItem value="all">Every active member</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Subject" value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth size="small" sx={{ mb: 2 }}
        />
        <TextField
          label="Message" value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth multiline minRows={6} maxRows={12}
          helperText="Plain text — paragraph breaks are preserved in the email."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!subject.trim() || !body.trim() || send.isPending}
          onClick={() => send.mutate()}
          sx={{ borderRadius: 999, px: 2.5 }}
        >
          {send.isPending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Delete dialog ──────────────────────────────────────────────────────────

function DeleteDialog({
  open, onClose, slug, orgName, onDeleted,
}: {
  open: boolean
  onClose: () => void
  slug: string
  orgName: string
  onDeleted: () => void
}) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [notify, setNotify] = useState(true)
  const [confirm, setConfirm] = useState('')

  const del = useMutation({
    mutationFn: () => api.superAdmin.deleteOrg(slug, {
      reason: reason.trim() || undefined,
      notify,
    }),
    onSuccess: () => {
      toast.success(`${orgName} deleted`)
      qc.invalidateQueries({ queryKey: ['super-admin', 'orgs'] })
      onClose()
      onDeleted()
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  })

  const canConfirm = confirm.trim() === orgName.trim()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, color: 'error.main' }}>
        Delete {orgName}?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 13, mb: 2 }}>
          This wipes <strong>{orgName}</strong> and every record inside it — users,
          assets, work orders, requests, audit logs. This cannot be undone.
        </DialogContentText>
        <TextField
          label="Reason (optional, included in the email)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth multiline minRows={2} maxRows={5}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
            />
          }
          label="Email the org admin first"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
        />
        <Box sx={{ mt: 2 }}>
          <TextField
            label={`Type "${orgName}" to confirm`}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth size="small" autoComplete="off"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="error" variant="contained"
          disabled={!canConfirm || del.isPending}
          onClick={() => del.mutate()}
          sx={{ borderRadius: 999, px: 2.5 }}
        >
          {del.isPending ? 'Deleting…' : 'Delete forever'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
